import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Code,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api, descargar } from '../api';
import { avisarError, avisarOk, Cabecera, CampoEuros, CampoPorcentaje, EJERCICIO_ACTUAL, OPCIONES_EJERCICIO } from '../comun';
import type { PerfilFiscal } from '../tipos';
import { PaginaCambiarClave } from './Acceso';
import { SeguridadCuenta } from '../componentes/DobleFactor';
import { TelegramVinculo } from '../componentes/TelegramVinculo';
import { NormativaEditable } from '../componentes/NormativaEditable';
import { useSesion } from '../sesion';

const aISO = (d: Date | string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : null);

export function PaginaAjustes() {
  return (
    <>
      <Cabecera titulo="Ajustes" />
      <Tabs defaultValue="negocio" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="negocio">Negocio</Tabs.Tab>
          <Tabs.Tab value="fiscal">Perfil fiscal</Tabs.Tab>
          <Tabs.Tab value="normativa">Normativa</Tabs.Tab>
          <Tabs.Tab value="avisos">Avisos</Tabs.Tab>
          <Tabs.Tab value="miembros">Personas</Tabs.Tab>
          <Tabs.Tab value="datos">Mis datos</Tabs.Tab>
          <Tabs.Tab value="cuenta">Mi cuenta</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="negocio">
          <Negocio />
        </Tabs.Panel>
        <Tabs.Panel value="fiscal">
          <Perfil />
        </Tabs.Panel>
        <Tabs.Panel value="normativa">
          <NormativaEditable />
        </Tabs.Panel>
        <Tabs.Panel value="avisos">
          <PreferenciasAvisos />
        </Tabs.Panel>
        <Tabs.Panel value="miembros">
          <Miembros />
        </Tabs.Panel>
        <Tabs.Panel value="datos">
          <Datos />
        </Tabs.Panel>
        <Tabs.Panel value="cuenta">
          <Stack>
            <SeguridadCuenta />
            <TelegramVinculo />
            <Paper withBorder p="md" maw={560}>
              <Text fw={600} mb="xs">
                Cambiar la contraseña
              </Text>
              <PaginaCambiarClave />
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

function Negocio() {
  const qc = useQueryClient();
  const n = useQuery({ queryKey: ['negocio'], queryFn: () => api.get<Record<string, string | null>>('/negocio') });
  const [d, setD] = useState<Record<string, string>>({});
  useEffect(() => {
    if (n.data) setD(Object.fromEntries(['nombre', 'nif', 'nombreFiscal', 'domicilioFiscal', 'isla'].map((k) => [k, n.data[k] ?? ''])));
  }, [n.data]);
  const guardar = async () => {
    try {
      const r = await api.put<{ aviso?: string }>('/negocio', Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v || null])));
      avisarOk(r.aviso ?? 'Datos guardados');
      await qc.invalidateQueries({ queryKey: ['negocio'] });
    } catch (e) {
      avisarError(e);
    }
  };
  const campo = (k: string) => ({ value: d[k] ?? '', onChange: (e: React.ChangeEvent<HTMLInputElement>) => setD({ ...d, [k]: e.currentTarget.value }) });
  return (
    <Paper withBorder p="md" maw={640}>
      <Stack>
        <TextInput label="Nombre del negocio" {...campo('nombre')} />
        <SimpleGrid cols={{ base: 1, xs: 2 }}>
          <TextInput label="NIF del titular" {...campo('nif')} />
          <TextInput label="Nombre fiscal" description="APELLIDOS, NOMBRE (como en Hacienda)" {...campo('nombreFiscal')} />
        </SimpleGrid>
        <TextInput label="Domicilio fiscal" {...campo('domicilioFiscal')} />
        <Select
          label="Isla"
          data={['Gran Canaria', 'Tenerife', 'Lanzarote', 'Fuerteventura', 'La Palma', 'La Gomera', 'El Hierro', 'La Graciosa']}
          value={d.isla || null}
          onChange={(v) => setD({ ...d, isla: v ?? '' })}
          clearable
        />
        <Text size="xs" c="dimmed">
          El NIF y el nombre fiscal se usan en el nombre del fichero de libros registro que se importa en la sede de la AEAT.
        </Text>
        <Group justify="flex-end">
          <Button onClick={guardar}>Guardar</Button>
        </Group>
      </Stack>
    </Paper>
  );
}

function Perfil() {
  const qc = useQueryClient();
  const [ejercicio, setEjercicio] = useState(String(EJERCICIO_ACTUAL));
  const p = useQuery({ queryKey: ['perfil', ejercicio], queryFn: () => api.get<PerfilFiscal>(`/negocio/perfil-fiscal/${ejercicio}`) });
  const [d, setD] = useState<PerfilFiscal | null>(null);
  useEffect(() => {
    if (p.data) setD({ ...p.data, ambito: p.data.ambito ?? 'general', exoneradoPago130: p.data.exoneradoPago130 ?? false });
  }, [p.data]);
  if (!d) return <Text>Cargando…</Text>;
  const guardar = async () => {
    try {
      await api.put(`/negocio/perfil-fiscal/${ejercicio}`, {
        actividadCodigo: d.actividadCodigo,
        actividadTipo: d.actividadTipo,
        epigrafe: d.epigrafe,
        regimenIrpf: d.regimenIrpf,
        regimenIgic: d.regimenIgic,
        fechaAltaActividad: d.fechaAltaActividad,
        retencionPorDefecto: d.retencionPorDefecto,
        ambito: d.ambito,
        rendimientoNetoAnterior: d.rendimientoNetoAnterior,
        exoneradoPago130: d.exoneradoPago130,
      });
      avisarOk(`Perfil fiscal de ${ejercicio} guardado`);
      await qc.invalidateQueries();
    } catch (e) {
      avisarError(e);
    }
  };
  return (
    <Paper withBorder p="md" maw={720}>
      <Stack>
        <Group>
          <Select label="Ejercicio" w={120} data={OPCIONES_EJERCICIO} value={ejercicio} onChange={(v) => setEjercicio(v ?? String(EJERCICIO_ACTUAL))} allowDeselect={false} />
          {!p.data?.existe && <Alert color="yellow">Este ejercicio aún no tiene perfil: se propone el del año anterior. Guárdalo para usarlo.</Alert>}
        </Group>
        <SimpleGrid cols={{ base: 1, xs: 3 }}>
          <Select label="Actividad" data={['A', 'B', 'C', 'D']} value={d.actividadCodigo} onChange={(v) => setD({ ...d, actividadCodigo: v ?? 'A' })} allowDeselect={false} />
          <Select
            label="Tipo"
            data={[
              { value: '03', label: '03 · Empresarial' },
              { value: '04', label: '04 · Profesional artística' },
              { value: '05', label: '05 · Resto profesionales' },
            ]}
            value={d.actividadTipo}
            onChange={(v) => setD({ ...d, actividadTipo: v ?? '05' })}
            allowDeselect={false}
          />
          <TextInput label="Epígrafe IAE" value={d.epigrafe} onChange={(e) => setD({ ...d, epigrafe: e.currentTarget.value })} />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, xs: 2 }}>
          <Select
            label="IRPF"
            data={[
              { value: 'EDS', label: 'Estimación directa simplificada' },
              { value: 'EDN', label: 'Estimación directa normal' },
              { value: 'EO', label: 'Estimación objetiva (módulos)' },
            ]}
            value={d.regimenIrpf}
            onChange={(v) => setD({ ...d, regimenIrpf: (v ?? 'EDS') as PerfilFiscal['regimenIrpf'] })}
            allowDeselect={false}
          />
          <Select
            label="IGIC"
            data={[
              { value: 'REPEP', label: 'Pequeño empresario (REPEP)' },
              { value: 'GENERAL', label: 'Régimen general' },
              { value: 'SIMPLIFICADO', label: 'Régimen simplificado' },
              { value: 'COMERCIANTE_MINORISTA', label: 'Comerciante minorista' },
            ]}
            value={d.regimenIgic}
            onChange={(v) => setD({ ...d, regimenIgic: (v ?? 'REPEP') as PerfilFiscal['regimenIgic'] })}
            allowDeselect={false}
          />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, xs: 3 }}>
          <DateInput label="Fecha de alta en la actividad" value={d.fechaAltaActividad} onChange={(v) => setD({ ...d, fechaAltaActividad: aISO(v) })} valueFormat="DD/MM/YYYY" clearable />
          <CampoPorcentaje label="Retención habitual en facturas" valor={d.retencionPorDefecto} onCambio={(v) => setD({ ...d, retencionPorDefecto: v })} />
          <Select
            label="Ámbito del 130"
            data={[
              { value: 'general', label: 'General (20 %)' },
              { value: 'la_palma', label: 'La Palma (8 % en 3T y 4T 2026)' },
              { value: 'ceuta_melilla', label: 'Ceuta y Melilla' },
            ]}
            value={d.ambito}
            onChange={(v) => setD({ ...d, ambito: (v ?? 'general') as PerfilFiscal['ambito'] })}
            allowDeselect={false}
          />
        </SimpleGrid>
        <CampoEuros
          label={`Rendimiento neto de actividades de ${Number(ejercicio) - 1}`}
          description="Sale de tu renta del año anterior. Si es de 12.000 € o menos, el 130 aplica una minoración (hasta 100 € por trimestre). Déjalo vacío si no tuviste actividad."
          valor={d.rendimientoNetoAnterior}
          onCambio={(v) => setD({ ...d, rendimientoNetoAnterior: v })}
          allowNegative
          maw={320}
        />
        <Checkbox
          label="Exonerado del modelo 130 (actividad profesional con al menos el 70 % de ingresos con retención el año anterior)"
          checked={d.exoneradoPago130}
          onChange={(e) => setD({ ...d, exoneradoPago130: e.currentTarget.checked })}
        />
        <Group justify="flex-end">
          <Button onClick={guardar}>Guardar perfil de {ejercicio}</Button>
        </Group>
      </Stack>
    </Paper>
  );
}

interface Preferencia {
  tipo: string;
  nombre: string;
  activa: boolean;
  antelacionDias: number;
  canalApp: boolean;
  canalEmail: boolean;
  canalTelegram: boolean;
}

function PreferenciasAvisos() {
  const qc = useQueryClient();
  const canales = useQuery({ queryKey: ['canales-avisos'], queryFn: () => api.get<{ telegram: boolean; email: boolean }>('/avisos/canales') });
  const p = useQuery({ queryKey: ['preferencias-avisos'], queryFn: () => api.get<Preferencia[]>('/negocio/preferencias-avisos') });
  const [lista, setLista] = useState<Preferencia[]>([]);
  useEffect(() => {
    if (p.data) setLista(p.data);
  }, [p.data]);
  const cambiar = (i: number, cambios: Partial<Preferencia>) => setLista(lista.map((x, n) => (n === i ? { ...x, ...cambios } : x)));
  const guardar = async () => {
    try {
      await api.put('/negocio/preferencias-avisos', lista.map(({ nombre: _n, ...resto }) => resto));
      avisarOk('Preferencias guardadas');
      await qc.invalidateQueries({ queryKey: ['preferencias-avisos'] });
      await qc.invalidateQueries({ queryKey: ['avisos'] });
    } catch (e) {
      avisarError(e);
    }
  };
  return (
    <Paper withBorder p="md">
      <Table.ScrollContainer minWidth={600}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Aviso</Table.Th>
              <Table.Th>Activo</Table.Th>
              <Table.Th>Antelación / días</Table.Th>
              <Table.Th>En la aplicación</Table.Th>
              <Table.Th>Telegram</Table.Th>
              <Table.Th>Correo</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lista.map((x, i) => (
              <Table.Tr key={x.tipo}>
                <Table.Td>{x.nombre}</Table.Td>
                <Table.Td>
                  <Switch checked={x.activa} onChange={(e) => cambiar(i, { activa: e.currentTarget.checked })} />
                </Table.Td>
                <Table.Td>
                  <NumberInput w={90} min={0} max={120} value={x.antelacionDias} onChange={(v) => cambiar(i, { antelacionDias: Number(v) || 0 })} />
                </Table.Td>
                <Table.Td>
                  <Checkbox checked={x.canalApp} onChange={(e) => cambiar(i, { canalApp: e.currentTarget.checked })} />
                </Table.Td>
                <Table.Td>
                  <Checkbox checked={x.canalTelegram} disabled={canales.data?.telegram === false} onChange={(e) => cambiar(i, { canalTelegram: e.currentTarget.checked })} />
                </Table.Td>
                <Table.Td>
                  <Checkbox checked={x.canalEmail} disabled={canales.data?.email === false} onChange={(e) => cambiar(i, { canalEmail: e.currentTarget.checked })} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {canales.data && !canales.data.telegram && (
        <Alert color="yellow" mt="sm">
          Telegram no está configurado en el servidor todavía. Cuando lo esté, vincula tu cuenta en <b>Mi cuenta</b>.
        </Alert>
      )}
      {canales.data && !canales.data.email && (
        <Text size="xs" c="dimmed" mt="xs">
          El correo no está configurado en el servidor: los avisos salen por Telegram y en la aplicación.
        </Text>
      )}
      <Text size="xs" c="dimmed" mt="xs">
        «Antelación»: días antes del plazo o vencimiento en que empieza el aviso; para los gastos pendientes, días que deben pasar desde que se anotaron.
      </Text>
      <Group justify="flex-end" mt="md">
        <Button onClick={guardar}>Guardar</Button>
      </Group>
    </Paper>
  );
}

function Miembros() {
  const qc = useQueryClient();
  const s = useSesion();
  const lista = useQuery({
    queryKey: ['miembros'],
    queryFn: () => api.get<{ id: string; nombre: string; email: string; rol: string; ultimoAcceso: string | null; totpActivo: boolean; telegram: boolean }[]>('/negocio/miembros'),
  });
  const [d, setD] = useState({ nombre: '', email: '', rol: 'titular' });
  const [clave, setClave] = useState<{ para: string; clave: string } | null>(null);
  const [aRestablecer, setARestablecer] = useState<{ id: string; nombre: string } | null>(null);

  const restablecer = async () => {
    if (!aRestablecer) return;
    try {
      const r = await api.post<{ claveTemporal: string }>(`/negocio/miembros/${aRestablecer.id}/restablecer`);
      setClave({ para: aRestablecer.nombre, clave: r.claveTemporal });
      setARestablecer(null);
      await qc.invalidateQueries({ queryKey: ['miembros'] });
    } catch (e) {
      avisarError(e);
    }
  };
  const anadir = async () => {
    try {
      const r = await api.post<{ claveTemporal: string | null }>('/negocio/miembros', d);
      setClave(r.claveTemporal ? { para: d.nombre, clave: r.claveTemporal } : null);
      setD({ nombre: '', email: '', rol: 'titular' });
      await qc.invalidateQueries({ queryKey: ['miembros'] });
      avisarOk('Acceso concedido');
    } catch (e) {
      avisarError(e);
    }
  };
  return (
    <Stack maw={720}>
      <Paper withBorder>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nombre</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Rol</Table.Th>
              <Table.Th>2 pasos</Table.Th>
              <Table.Th>Telegram</Table.Th>
              <Table.Th>Último acceso</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(lista.data ?? []).map((m) => (
              <Table.Tr key={m.id}>
                <Table.Td>{m.nombre}</Table.Td>
                <Table.Td>{m.email}</Table.Td>
                <Table.Td>{m.rol}</Table.Td>
                <Table.Td>
                  <Badge color={m.totpActivo ? 'teal' : 'gray'} variant="light" size="sm">
                    {m.totpActivo ? 'Sí' : 'No'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={m.telegram ? 'teal' : 'gray'} variant="light" size="sm">
                    {m.telegram ? 'Sí' : 'No'}
                  </Badge>
                </Table.Td>
                <Table.Td>{m.ultimoAcceso ? dayjs(m.ultimoAcceso).format('DD/MM/YYYY HH:mm') : 'Nunca'}</Table.Td>
                <Table.Td>
                  {m.id !== s.usuario?.id && (
                    <Button size="xs" variant="subtle" color="gray" onClick={() => setARestablecer({ id: m.id, nombre: m.nombre })}>
                      Restablecer acceso
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      <Paper withBorder p="md">
        <Text fw={600} mb="xs">
          Dar acceso a otra persona
        </Text>
        <SimpleGrid cols={{ base: 1, xs: 3 }}>
          <TextInput label="Nombre" value={d.nombre} onChange={(e) => setD({ ...d, nombre: e.currentTarget.value })} />
          <TextInput label="Email" type="email" value={d.email} onChange={(e) => setD({ ...d, email: e.currentTarget.value })} />
          <Select
            label="Rol"
            data={[
              { value: 'titular', label: 'Titular' },
              { value: 'gestor', label: 'Gestor' },
            ]}
            value={d.rol}
            onChange={(v) => setD({ ...d, rol: v ?? 'gestor' })}
            allowDeselect={false}
          />
        </SimpleGrid>
        <Group justify="flex-end" mt="md">
          <Button onClick={anadir} disabled={!d.nombre || !d.email}>
            Dar acceso
          </Button>
        </Group>
        {clave && (
          <Alert color="blue" mt="md" withCloseButton onClose={() => setClave(null)}>
            Contraseña temporal de <b>{clave.para}</b> (solo se muestra ahora; tendrá que cambiarla al entrar): <Code>{clave.clave}</Code>
          </Alert>
        )}
      </Paper>
      <Modal opened={aRestablecer !== null} onClose={() => setARestablecer(null)} title="Restablecer acceso">
        <Stack>
          <Text size="sm">
            Se creará una contraseña temporal para <b>{aRestablecer?.nombre}</b>, se <b>desactivará su verificación en dos pasos</b> y se cerrarán sus sesiones. Tendrá que elegir una contraseña nueva y volver a activar el doble factor. Úsalo si ha olvidado la contraseña o ha perdido el móvil.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setARestablecer(null)}>
              Cancelar
            </Button>
            <Button color="red" onClick={restablecer}>
              Restablecer
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function Datos() {
  const [bajando, setBajando] = useState(false);
  const auditoria = useQuery({
    queryKey: ['auditoria'],
    queryFn: () => api.get<{ id: number; fecha: string; usuario: string | null; entidad: string; accion: string }[]>('/auditoria'),
  });
  return (
    <Stack maw={820}>
      <Paper withBorder p="md">
        <Text fw={600}>Exportar todos los datos</Text>
        <Text size="sm" c="dimmed" mb="sm">
          Un archivo ZIP con todos los movimientos, terceros, declaraciones y documentos originales, en formatos abiertos (JSON y CSV). Guárdalo como copia propia.
        </Text>
        <Button
          loading={bajando}
          onClick={async () => {
            setBajando(true);
            await descargar('/exportacion/completa', 'gestionpyme.zip').catch(avisarError);
            setBajando(false);
          }}
        >
          Descargar exportación completa
        </Button>
      </Paper>
      <Paper withBorder p="md">
        <Text fw={600} mb="xs">
          Últimos cambios (auditoría)
        </Text>
        <Table.ScrollContainer minWidth={500}>
          <Table verticalSpacing={4}>
            <Table.Tbody>
              {(auditoria.data ?? []).map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td>
                    <Text size="xs">{dayjs(e.fecha).format('DD/MM/YYYY HH:mm')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{e.usuario ?? 'Sistema'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {e.entidad} · {e.accion.replace(/_/g, ' ')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
